// Duplicate-batch handling for purchase imports (Purchase Invoice / GRN).
//
// 1. mergeImportedBatches: a vendor file often lists the same batch on several
//    lines. Lines that are the same batch at the same expiry and the same
//    prices are one batch - their quantities and amounts are added together.
//    The same product on several rows is also folded into one row first.
// 2. findBatchConflicts: the same batch of a product must have one expiry and
//    one price. Anything else is listed case by case, so Save can stop on the
//    first and the user fixes them one at a time.

const norm = (v) => `${v ?? ""}`.trim().toUpperCase();
const day = (v) => `${v ?? ""}`.trim().slice(0, 10);
const num = (v) => {
  const n = parseFloat(v);
  return Number.isFinite(n) ? n : 0;
};
const round2 = (v) => +num(v).toFixed(2);

// Amount / quantity fields that add up when two lines are the same batch.
const ADDITIVE_BATCH_FIELDS = [
  "item_sellable_quantity",
  "item_batch_free_quantity",
  "item_batch_total_effective_qauntity",
  "item_batch_total_base_purchase_rate",
  "item_batch_scheme_discount_amount",
  "item_batch_scheme_purchase_rate",
  "item_batch_gen_discount_amount",
  "item_batch_discounted_purchase_rate",
  "item_batch_discount_amount",
  "item_batch_taxable_amount",
  "item_batch_tax_amount",
  "item_batch_total_purchase_rate",
  "item_batch_cgst_amount",
  "item_batch_sgst_amount",
  "item_batch_igst_amount",
];

const ADDITIVE_ITEM_FIELDS = [
  "item_quantity",
  "item_free_quantity",
  "item_discount_amount",
  "item_tax_amount",
  "item_total_amount",
  "item_cgst",
  "item_sgst",
  "item_igst",
];

// Two lines are the same batch only when all of these match.
const batchIdentity = (b) =>
  [
    norm(b?.item_batch_number),
    day(b?.item_exp_date),
    num(b?.item_batch_purchase_rate),
    num(b?.item_batch_unit_price),
    num(b?.item_batch_discount_percentage),
    num(b?.item_batch_scheme_discount_percentage),
    num(b?.item_batch_for_qauntity),
    num(b?.item_batch_for_free_qauntity),
    num(b?.item_batch_tax_percentage),
    b?.to_bin_id ?? "",
  ].join("|");

const sumInto = (target, source, fields) => {
  fields.forEach((f) => {
    if (target[f] === undefined && source[f] === undefined) return;
    target[f] = round2(num(target[f]) + num(source[f]));
  });
  return target;
};

const mergeBatches = (batches = []) => {
  const merged = [];
  const byKey = new Map();
  batches.forEach((batch) => {
    const key = batchIdentity(batch);
    if (byKey.has(key)) {
      sumInto(byKey.get(key), batch, ADDITIVE_BATCH_FIELDS);
    } else {
      const copy = { ...batch };
      byKey.set(key, copy);
      merged.push(copy);
    }
  });
  return merged;
};

// Imported items -> one row per product (and UOM), identical batches clubbed.
// Rows whose product wasn't matched (no item_id) are left exactly as they are.
export const mergeImportedBatches = (items = []) => {
  const rows = [];
  const byProduct = new Map();
  items.forEach((item) => {
    if (!item?.item_id) {
      rows.push({ ...item });
      return;
    }
    const key = `${item.item_id}|${item?.item_uom_id ?? ""}`;
    const existing = byProduct.get(key);
    if (existing) {
      sumInto(existing, item, ADDITIVE_ITEM_FIELDS);
      existing.itemBatchData = [
        ...(existing.itemBatchData || []),
        ...(item.itemBatchData || []),
      ];
    } else {
      const copy = { ...item, itemBatchData: [...(item.itemBatchData || [])] };
      byProduct.set(key, copy);
      rows.push(copy);
    }
  });
  return rows.map((row) =>
    Array.isArray(row.itemBatchData)
      ? { ...row, itemBatchData: mergeBatches(row.itemBatchData) }
      : row
  );
};

const CHECKS = [
  {
    label: "expiry date",
    plural: "expiry dates",
    get: (b) => day(b?.item_exp_date),
    show: (v) => v || "blank",
  },
  {
    label: "purchase rate",
    plural: "purchase rates",
    get: (b) => num(b?.item_batch_purchase_rate),
    show: (v) => `₹${v}`,
  },
  {
    label: "MRP",
    plural: "MRPs",
    get: (b) => num(b?.item_batch_unit_price),
    show: (v) => `₹${v}`,
  },
];

// Every "same batch, different expiry / price" case in the form's rows, in
// row order. Same batch number on two different products is fine.
export const findBatchConflicts = (rows = []) => {
  const seen = new Map(); // item_id|batch -> { row, batch }
  const cases = [];
  rows.forEach((row, rowIndex) => {
    if (!row?.item_id) return;
    (row.item_batches || []).forEach((batch) => {
      const batchNo = norm(batch?.item_batch_number);
      if (!batchNo) return;
      const key = `${row.item_id}|${batchNo}`;
      const first = seen.get(key);
      if (!first) {
        seen.set(key, { rowIndex, batch });
        return;
      }
      CHECKS.forEach((c) => {
        const a = c.get(first.batch);
        const b = c.get(batch);
        if (a === b) return;
        const rowsText =
          first.rowIndex === rowIndex
            ? `row ${rowIndex + 1}`
            : `rows ${first.rowIndex + 1} and ${rowIndex + 1}`;
        cases.push(
          `${row.item_name || "Item"} (${rowsText}): batch ${
            batch.item_batch_number
          } has two ${c.plural} - ${c.show(a)} and ${c.show(b)}. One batch must have one ${
            c.label
          }.`
        );
      });
    });
  });
  return cases;
};
